import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const STORAGE_KEY = 'cardlockr_tour_completed';

export default function OnboardingTour() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    const key = `${STORAGE_KEY}_${user.id}`;
    if (!localStorage.getItem(key)) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const completeTour = useCallback(() => {
    if (user) localStorage.setItem(`${STORAGE_KEY}_${user.id}`, 'true');
    setVisible(false);
  }, [user]);

  const steps = [
    {
      icon: 'fa-hand-sparkles',
      title: t.tour.welcomeTitle,
      desc: t.tour.welcomeDesc,
      color: '#4F46E5',
    },
    {
      icon: 'fa-chart-pie',
      title: t.tour.dashboardTitle,
      desc: t.tour.dashboardDesc,
      color: '#3B82F6',
      action: () => navigate('/dashboard'),
    },
    {
      icon: 'fa-store',
      title: t.tour.suppliersTitle,
      desc: t.tour.suppliersDesc,
      color: '#10B981',
      action: () => navigate('/dashboard/suppliers'),
    },
    {
      icon: 'fa-gift',
      title: t.tour.giftCardsTitle,
      desc: t.tour.giftCardsDesc,
      color: '#F59E0B',
      action: () => navigate('/dashboard/gift-cards'),
    },
    {
      icon: 'fa-exchange-alt',
      title: t.tour.transactionsTitle,
      desc: t.tour.transactionsDesc,
      color: '#8B5CF6',
      action: () => navigate('/dashboard/transactions'),
    },
    {
      icon: 'fa-file-alt',
      title: t.tour.reportsTitle,
      desc: t.tour.reportsDesc,
      color: '#EC4899',
      action: () => navigate('/dashboard/reports'),
    },
    {
      icon: 'fa-cog',
      title: t.tour.settingsTitle,
      desc: t.tour.settingsDesc,
      color: '#64748B',
      action: () => navigate('/dashboard/settings'),
    },
    {
      icon: 'fa-rocket',
      title: t.tour.readyTitle,
      desc: t.tour.readyDesc,
      color: '#4F46E5',
    },
  ];

  const currentStep = steps[step];
  const isLast = step === steps.length - 1;
  const isFirst = step === 0;

  const handleNext = () => {
    if (isLast) {
      completeTour();
      navigate('/dashboard');
    } else {
      const next = step + 1;
      setStep(next);
      if (steps[next].action) steps[next].action();
    }
  };

  const handleBack = () => {
    if (!isFirst) {
      const prev = step - 1;
      setStep(prev);
      if (steps[prev].action) steps[prev].action();
    }
  };

  if (!visible) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        {/* Progress bar */}
        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressBar, width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>

        {/* Step counter */}
        <div style={styles.stepCounter}>
          {step + 1} / {steps.length}
        </div>

        {/* Icon */}
        <div style={{ ...styles.iconCircle, background: `linear-gradient(135deg, ${currentStep.color}, ${currentStep.color}dd)` }}>
          <i className={`fas ${currentStep.icon}`} style={{ fontSize: '1.8rem', color: 'white' }} />
        </div>

        {/* Content */}
        <h2 style={styles.title}>{currentStep.title}</h2>
        <p style={styles.desc}>{currentStep.desc}</p>

        {/* Dots */}
        <div style={styles.dots}>
          {steps.map((_, i) => (
            <div
              key={i}
              style={{
                ...styles.dot,
                background: i === step ? '#4F46E5' : i < step ? '#A5B4FC' : '#E2E8F0',
                width: i === step ? 24 : 8,
              }}
            />
          ))}
        </div>

        {/* Buttons */}
        <div style={styles.buttons}>
          {isFirst ? (
            <button onClick={completeTour} style={styles.skipBtn}>
              {t.tour.skip}
            </button>
          ) : (
            <button onClick={handleBack} style={styles.backBtn}>
              <i className="fas fa-chevron-left" style={{ marginRight: 6, fontSize: '0.75rem' }} />
              {t.back}
            </button>
          )}
          <button onClick={handleNext} style={styles.nextBtn}>
            {isLast ? t.tour.start : t.tour.next}
            {!isLast && <i className="fas fa-chevron-right" style={{ marginLeft: 6, fontSize: '0.75rem' }} />}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.7)',
    backdropFilter: 'blur(8px)',
    zIndex: 3000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    animation: 'tourFadeIn 0.3s ease',
  },
  card: {
    background: 'white',
    borderRadius: 20,
    padding: '2rem 2.5rem',
    maxWidth: 480,
    width: '100%',
    textAlign: 'center',
    position: 'relative',
    boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
    animation: 'tourSlideUp 0.4s ease',
  },
  progressTrack: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: '#E2E8F0',
    borderRadius: '20px 20px 0 0',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    background: 'linear-gradient(90deg, #4F46E5, #7C3AED)',
    transition: 'width 0.4s ease',
    borderRadius: '20px 20px 0 0',
  },
  stepCounter: {
    fontSize: '0.7rem',
    color: '#94A3B8',
    fontWeight: 600,
    marginBottom: '1.5rem',
    marginTop: '0.5rem',
    letterSpacing: '0.05em',
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1.25rem',
    boxShadow: '0 8px 24px rgba(79, 70, 229, 0.25)',
  },
  title: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#0F172A',
    marginBottom: '0.5rem',
    letterSpacing: '-0.02em',
  },
  desc: {
    fontSize: '0.9rem',
    color: '#64748B',
    lineHeight: 1.6,
    marginBottom: '1.5rem',
    maxWidth: 380,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: 6,
    marginBottom: '1.5rem',
  },
  dot: {
    height: 8,
    borderRadius: 4,
    transition: 'all 0.3s ease',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem',
  },
  skipBtn: {
    background: 'none',
    border: 'none',
    color: '#94A3B8',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    padding: '0.5rem 1rem',
    borderRadius: 8,
    transition: 'all 0.2s ease',
  },
  backBtn: {
    background: '#F1F5F9',
    border: 'none',
    color: '#64748B',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    padding: '0.6rem 1.25rem',
    borderRadius: 10,
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
  },
  nextBtn: {
    background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
    border: 'none',
    color: 'white',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    padding: '0.6rem 1.5rem',
    borderRadius: 10,
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
};
